<template>
  <div>
    <!-- Company Logo -->
    <company-logo />
    <!-- Loading Spinner -->
    <loading-spinner v-if="isLoading" />

    <!-- Login Form -->
    <Form @submit="handleLogin" :validation-schema="schema">
      <!-- Phone number Input -->
      <div class="mt-3 ms-4 me-4">
        <Field name="phone" v-slot="{ handleChange, errorMessage }">
          <phone-input-field
            v-model:phoneNumber="enteredPhoneNumber"
            @update:phoneNumber="handleChange"
            :errorValidate="errorMessage"
            @country-code="updateCountryCode"
          />
        </Field>
      </div>

      <!-- Password Input -->
      <div class="mt-3 ms-4 me-4">
        <Field name="password" v-slot="{ handleChange, errorMessage }">
          <default-input-field
            type="password"
            placeholder="Password"
            v-model:value.trim="password"
            @update:value="handleChange"
            :errorValidate="errorMessage"
          />
        </Field>
      </div>
      <div class="text-start mt-2 ms-4 me-4 scss-text-blue">
        <span class="scss-click-effect" @click="goToForgotPassword">
          Forgot password?
        </span>
      </div>
      <div class="ms-3 me-3 mt-5">
        <div class="style-font-size">
          By proceeding, you agree to our
          <span
            class="scss-click-effect scss-text-blue"
            @click="goToLink('https://wessconnect.com/term-of-service/')"
            >Terms & Conditions</span
          >.
        </div>
        <default-button title="Login" type="submit" />
        <div class="style-font-size">
          Don't have an account yet?
          <span class="scss-click-effect scss-text-blue" @click="goToSignUp"
            >Sign up</span
          >
        </div>
      </div>
    </Form>
  </div>
</template>

<script>
import * as yup from "yup";
import { Form, Field } from "vee-validate";
import { useToast } from "vue-toastification";
import { mapGetters, mapMutations } from "vuex";

import DefaultButton from "@/components/button/DefaultButton.vue";
import PhoneInputField from "@/components/phoneInput/PhoneInputField.vue";
import DefaultInputField from "@/components/input/DefaultInputField.vue";
import LoadingSpinner from "@/components/LoadingSpinner";
import CompanyLogo from "@/components/CompanyLogo.vue";
import { validationRules } from "@/validation/ValidationRules";
import { login } from "@/services/auth";
import { saveToken, removeToken } from "@/services/jwtService";
import { saveLoginBranch, removeLoginBranch } from "@/services/loginBranch";

export default {
  components: {
    Form,
    Field,
    DefaultButton,
    PhoneInputField,
    DefaultInputField,
    LoadingSpinner,
    CompanyLogo,
  },
  name: "Login",
  data() {
    return {
      enteredPhoneNumber: "",
      password: "",
      toastMessage: false,
      isLoading: false,
      countryCode: "65",
    };
  },
  computed: {
    schema() {
      return yup.object({
        phone: validationRules.phone,
        password: validationRules.passwordLogin,
      });
    },
    phoneMobile() {
      return `+${this.countryCode.toString()}${this.enteredPhoneNumber}`;
    },
    ...mapGetters(["branches", "selectedBranch"]),
  },
  methods: {
    goToSignUp() {
      this.$router.push("/signup");
    },
    goToForgotPassword() {
      this.$router.push("/forgot-password");
    },
    goToLink(link) {
      const newTab = window;
      newTab.open(link, "_target");
    },
    updateCountryCode(value) {
      this.countryCode = value;
    },
    async handleLogin() {
      try {
        this.isLoading = true;
        const response = await login(this.phoneMobile, this.password);
        if (response.data) {
          this.isLoading = false;

          // 1. wessBranchId is customer registered branch (wess-legacy > customer [top nav] > customer > cwa[tab])
          // 2. branchIds is active branch ids (array)
          const { token, wessBranchId, branchIds } = response.data;
          let redirectHomePageAndToastSwitchBranch = false;
          // If cwa app selected branch is in response branchIds(customer active branch ids), login branch will be cwa app selected branch.
          let selectedLoginBranch = undefined;
          let toastMessage = `Outlet {notActiveBranch} is not activated, kindly contact the salon for assistance. You will be redirect to outlet {autoSelectBranch}.`;
          if (branchIds.includes(this.selectedBranch.id)) {
            selectedLoginBranch = this.branches.find(
              (branch) => branch.id === this.selectedBranch.id
            );
          } else if (branchIds.includes(wessBranchId)) {
            selectedLoginBranch = this.branches.find(
              (branch) => branch.id === wessBranchId
            );

            redirectHomePageAndToastSwitchBranch = true
            toastMessage = toastMessage
              .replace("{notActiveBranch}", `${this.selectedBranch.name}`)
              .replace("{autoSelectBranch}", `${selectedLoginBranch?.name}`);
          } else {
            selectedLoginBranch = this.branches.find(
              (branch) => branchIds.includes(branch.id)
            );

            redirectHomePageAndToastSwitchBranch = true;
            toastMessage = toastMessage
              .replace("{notActiveBranch}", `${this.selectedBranch.name}`)
              .replace("{autoSelectBranch}", `${selectedLoginBranch?.name}`);
          }

          // customer will consider successfully login only if
          // 1. has registered CWA account before
          // 2. valid username and password
          // 3. has a active branches to use
          // else consider failed to login
          if (selectedLoginBranch !== undefined) {
            // save login branch
            saveLoginBranch(selectedLoginBranch);
            // save token
            saveToken(token);
            await this.$store.dispatch("updateSelectedBranch", selectedLoginBranch);
            await this.$store.dispatch("getLoginUserInfo", null, { root: true });
            await this.$store.dispatch("getCustomerBranches", this.$store.getters.loginUserInfo.id)
          } else {
            throw new Error("No available active outlet.  kindly contact the salon for assistance.");
          }

          // Route to home or previous page
          if (this.$router.options.history.state.back) {
            if (redirectHomePageAndToastSwitchBranch === true) {
              this.$router.push("/home");
              useToast().error(toastMessage);
            } else {
              this.$router.go(-1);
            }
          } else {
            this.$router.push("/home");
          }
        }
      } catch (error) {
        removeLoginBranch();
        removeToken();
        this.isLoading = false;
        const errorMsg = error?.message ?
          error.message : "Invalid account or password.";

        useToast().error(errorMsg);
      }
    },
    ...mapMutations(["showLoading"]),
  },
  created() {
    this.showLoading(false);
  },
};
</script>
<style scoped>
.style-font-size {
  font-size: 14px;
}

.style-branch-container {
  font-size: 16px;
  border: 1px solid #adadad;
  cursor: pointer;
  height: 50px;
  background-color: white;
}

.style-branch-content {
  font-size: 16px;
  padding-top: 3px;
  padding-right: 10px;
}

.style-option-height {
  min-height: 50px;
}
</style>
